import { Auth } from "aws-amplify";
import { AlertTriangle, AlertCircle, Bell, CheckCircle, XCircle, Ban } from "lucide-react";

export function camelToTitle(str) {
  // 1) Replace underscores with a space
  let cleaned = str.replace(/_/g, " ");

  // 2) Trim whitespace
  cleaned = cleaned.trim();

  // 3) Collapse multiple spaces into one
  cleaned = cleaned.replace(/\s+/g, " ");

  // 4) Split on space so we can handle each chunk
  const words = cleaned.split(" ");

  // We'll process each chunk to handle CamelCase or ALLCAPS
  const processedWords = words.map((word) => {
    // If word is ALL CAPS (e.g., "ABC"), convert it to lowercase first
    if (/^[A-Z]+$/.test(word)) {
      word = word.toLowerCase();
    }
    // Insert a space before any uppercase letter that follows a lowercase or digit
    // e.g., "autoBots" => "auto Bots", "AutoBots" => "Auto Bots"
    word = word.replace(/([a-z\d])([A-Z])/g, "$1 $2");

    // Now split that chunk on space (in case there are multiple uppercase letters)
    // and Title Case each sub-chunk
    let subChunks = word.split(" ").map((sub) => {
      return sub.charAt(0).toUpperCase() + sub.slice(1).toLowerCase();
    });

    // Rejoin sub-chunks with space
    return subChunks.join(" ");
  });

  // 5) Rejoin all processed chunks with a space
  return processedWords.join(" ");
}

export async function getAccessToken() {
  try {
    const session = await Auth.currentSession();
    return session.getAccessToken().getJwtToken();
  } catch (err) {
    console.error("Error fetching access token", err);
    return null;
  }
}
export function capitalizeFirst(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function isNewTeam(startDate) {
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  return new Date(startDate) >= threeMonthsAgo;
}

export function isClosingSoon(endDate) {
  const now = new Date();
  const date = new Date(endDate);
  const diffInDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

  let days = null;
  if (diffInDays > 0 && diffInDays <= 30) {
    days = 30;
  } else if (diffInDays > 30 && diffInDays <= 60) {
    days = 60;
  } else if (diffInDays > 60 && diffInDays <= 90) {
    days = 90;
  }

  return {
    days,
    isClosingSoon: days !== null,
  };
}

export function renderClosingRibbon(endDate) {
  const { days } = isClosingSoon(endDate);
  if (!days) return null;

  return (
    <span className={'ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-800'}>
      Closing in {days} days
    </span>
  );
}

export function getStatusColor(health) {
  switch (health) {
    case 'Healthy':
      return 'text-green-600 bg-green-50 border border-green-300';
    case 'At Risk':
      return 'text-amber-600 bg-amber-50 border border-amber-300';
    case 'Critical':
      return 'text-red-600 bg-red-50 border border-red-300';
    case 'Start Strong':
      return 'text-blue-800 bg-blue-100 border border-blue-300';
    default:
      return 'text-gray-600 bg-gray-50 border border-gray-300';
  }
}

export function getStatusIcon(status) {
  switch (status) {
    case "Healthy":
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case "At Risk":
      return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    case "Critical":
      return <XCircle className="w-5 h-5 text-red-500" />;
    case "Start Strong":
      return <CheckCircle className="w-5 h-5 text-blue-500" />;
    default:
      return <Ban className="w-5 h-5 text-gray-500" />;
  }
}

export function getTouchpointStatus(lastDate, frequency) {
    if (!lastDate) {
        return { status: 'immediate', label: 'No Contact', color: 'bg-gray-100 text-gray-800', icon: AlertCircle };
    }
    const lastTouchpoint = new Date(lastDate);
    const now = new Date();
    const daysDiff = Math.floor((now.getTime() - lastTouchpoint.getTime()) / (24 * 60 * 60 * 1000));
    const frequencyDays = parseInt(frequency) * 7;
    const warningDays = frequencyDays - 14; // 2 weeks before

    if (daysDiff >= frequencyDays) {
        return { status: 'immediate', label: 'Immediate Attention', color: 'bg-red-100 text-red-800', icon: AlertCircle };
    } else if (daysDiff >= warningDays) {
        return { status: 'warning', label: 'Due Soon', color: 'bg-yellow-100 text-yellow-800', icon: Bell };
    } else {
        return { status: 'good', label: 'On Track', color: 'bg-green-100 text-green-800', icon: CheckCircle };
    }
};

export async function updateApi({data, customer = null, team = null, pk = null, gsiPk = null}) {
    try {
        const { updateTeamData } = await import('../config/ApiConfig');
        return await updateTeamData(data, customer, team, pk, gsiPk);
    } catch (error) {
        console.error('Error updating:', error);
        throw error;
    }
};
